import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { feeTotals, type FeeTotals } from "../lib/superadminFeeTotals";
import { formatMoney } from "../lib/money";
import { useAuth } from "../auth/AuthContext";
import {
  ApiError,
  SUPPORT_ROLE_LABEL,
  api,
  type AuthUser,
  type SuperadminDashboardPayload,
} from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { describeLastAccess } from "../lib/dateFormat";
import {
  archivedHiddenNote,
  splitArchived,
  superadminStatusLabel,
} from "../lib/superadminArchived";

export default function HomeSuperadminPage() {
  const { token, user, impersonateWithToken } = useAuth();
  const { askConfirm } = useAbortableConfirm([]);
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<SuperadminDashboardPayload | null>(
    null,
  );
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  // Solicitudes de demo sin atender (null = aún no se sabe o no cargó).
  const [newDemoRequests, setNewDemoRequests] = useState<number | null>(null);
  const [admins, setAdmins] = useState<AuthUser[]>([]);
  const [adminQuery, setAdminQuery] = useState("");
  // La lista llega completa (GET /superadmin/admins no pagina): filtro local.
  const visibleAdmins = useMemo(() => filterAdmins(admins, adminQuery), [admins, adminQuery]);
  const [listError, setListError] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resettingAdminId, setResettingAdminId] = useState<number | null>(null);
  const [impersonatingAdminId, setImpersonatingAdminId] = useState<
    number | null
  >(null);
  const [resetPasswords, setResetPasswords] = useState<Record<number, string>>({});
  const [statusBusyId, setStatusBusyId] = useState<number | null>(null);
  // Eliminar en DOS pasos: el primer clic arma, el segundo ejecuta.
  const [armedDeleteId, setArmedDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formMessage, setFormMessage] = useState<string | null>(null);
  // Rediseño 2026-10-09: pendientes y menú «⋯» por organizador.
  const [openTickets, setOpenTickets] = useState<number | null>(null);
  const [fees, setFees] = useState<FeeTotals | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [menuAdminId, setMenuAdminId] = useState<number | null>(null);
  const [resetOpenId, setResetOpenId] = useState<number | null>(null);

  const loadDashboard = useCallback(async () => {
    if (!token) return;
    setDashboardError(null);
    try {
      const response = await api.dashboards.superadmin(token);
      setDashboard(response.data);
    } catch (err) {
      setDashboardError(
        err instanceof ApiError
          ? err.message || "No pudimos cargar el dashboard."
          : "Error de red. Intenta otra vez.",
      );
    }
  }, [token]);

  const loadAdmins = useCallback(async () => {
    if (!token) return;
    setListError(null);
    try {
      const response = await api.superadmin.listAdmins(token);
      setAdmins(response.data.admins);
    } catch (err) {
      setListError(err instanceof Error ? err.message : "No pudimos cargar la lista.");
    }
  }, [token]);

  const loadDemoRequests = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.superadmin.demoRequests.list(token, { status: "nuevo", limit: 1 });
      setNewDemoRequests(response.data.counts.nuevo);
    } catch {
      // Sin aviso: la tarjeta muestra "—" y la bandeja tiene su propio error.
      setNewDemoRequests(null);
    }
  }, [token]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    void loadDemoRequests();
  }, [loadDemoRequests]);

  useEffect(() => {
    void loadAdmins();
  }, [loadAdmins]);

  // Pendientes de la portada (solo lectura): tickets abiertos y fees por
  // cobrar. Si fallan, la tarjeta muestra «—» y su pantalla tiene su error.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.supportTickets
      .list(token, "open")
      .then((response) => alive && setOpenTickets(response.data.tickets.length))
      .catch(() => alive && setOpenTickets(null));
    api.payments
      .tournamentFees(token)
      .then((response) => alive && setFees(feeTotals(response.data.tournaments)))
      .catch(() => alive && setFees(null));
    return () => {
      alive = false;
    };
  }, [token]);

  // Menú «⋯»: se cierra con Escape o al tocar fuera.
  useEffect(() => {
    if (menuAdminId === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuAdminId(null);
    }
    function onPointer(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target?.closest?.("[data-row-menu]")) setMenuAdminId(null);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [menuAdminId]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    setFormError(null);
    setFormMessage(null);
    setSubmitting(true);
    try {
      const response = await api.superadmin.createAdmin(token, {
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        password: password.trim(),
      });
      setFormMessage(`Admin creado: ${response.data.user.email}`);
      setFullName("");
      setEmail("");
      setPhone("");
      setPassword("");
      await loadAdmins();
      await loadDashboard();
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message || "No se pudo crear el admin.");
      } else {
        setFormError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetPassword(admin: AuthUser) {
    if (!token) return;
    const nextPassword = resetPasswords[admin.id]?.trim() ?? "";
    if (nextPassword.length < 6) {
      setFormError("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }
    setFormError(null);
    setFormMessage(null);
    setResettingAdminId(admin.id);
    try {
      await api.superadmin.resetAdminPassword(token, admin.id, nextPassword);
      setResetPasswords((prev) => ({ ...prev, [admin.id]: "" }));
      setFormMessage(`Contraseña actualizada para ${admin.email}.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message || "No se pudo actualizar la contraseña.");
      } else {
        setFormError("Error de red. Intenta otra vez.");
      }
    } finally {
      setResettingAdminId(null);
    }
  }

  async function handleToggleSupport(admin: AuthUser) {
    if (!token) return;
    const enable = !admin.roles.includes("soporte");
    const ok = await askConfirm(
      enable
        ? `¿Hacer a ${admin.full_name} ${SUPPORT_ROLE_LABEL}? Podrá entrar a TODOS los torneos y hacer lo mismo que el organizador, menos dinero, borrar torneos o cambiar su equipo. Los organizadores lo verán en el Equipo de cada torneo.`
        : `¿Quitarle ${SUPPORT_ROLE_LABEL} a ${admin.full_name}? Deja de entrar a los torneos de otros de inmediato.`,
      { acceptLabel: enable ? `Sí, hacerlo ${SUPPORT_ROLE_LABEL}` : "Sí, quitar", tone: enable ? undefined : "danger" },
    );
    if (!ok) return;
    setFormError(null);
    setFormMessage(null);
    setStatusBusyId(admin.id);
    try {
      await api.superadmin.setAdminSupport(token, admin.id, enable);
      setFormMessage(
        enable
          ? `${admin.full_name} ya es ${SUPPORT_ROLE_LABEL}.`
          : `${admin.full_name} ya no es ${SUPPORT_ROLE_LABEL}: perdió el acceso a los torneos de otros.`,
      );
      await loadAdmins();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message || "No se pudo cambiar el rol." : "Error de red. Intenta otra vez.",
      );
    } finally {
      setStatusBusyId(null);
    }
  }

  async function handleToggleStatus(admin: AuthUser) {
    if (!token) return;
    setFormError(null);
    setFormMessage(null);
    setStatusBusyId(admin.id);
    const next = admin.status === "disabled" ? "active" : "disabled";
    try {
      await api.superadmin.setAdminStatus(token, admin.id, next);
      setFormMessage(
        next === "disabled"
          ? `${admin.email} inactivado: su sesión deja de servir de inmediato.`
          : `${admin.email} reactivado.`,
      );
      await loadAdmins();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message || "No se pudo cambiar el estado." : "Error de red. Intenta otra vez.",
      );
    } finally {
      setStatusBusyId(null);
    }
  }

  async function handleDeleteAdmin(admin: AuthUser) {
    if (!token) return;
    if (armedDeleteId !== admin.id) {
      // Primer clic: armar. Cambiar de admin desarma el anterior.
      setArmedDeleteId(admin.id);
      return;
    }
    setFormError(null);
    setFormMessage(null);
    setDeletingId(admin.id);
    try {
      await api.superadmin.deleteAdmin(token, admin.id);
      setFormMessage(`${admin.email} eliminado.`);
      await loadAdmins();
      await loadDashboard();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message || "No se pudo eliminar." : "Error de red. Intenta otra vez.",
      );
    } finally {
      setDeletingId(null);
      setArmedDeleteId(null);
    }
  }

  async function handleImpersonateAdmin(admin: AuthUser) {
    if (!token) return;
    setFormError(null);
    setFormMessage(null);
    setImpersonatingAdminId(admin.id);
    try {
      const response = await api.superadmin.impersonateAdmin(token, admin.id);
      impersonateWithToken(response.data.access_token, response.data.user);
      navigate("/admin", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message || "No se pudo entrar como ese admin.");
      } else {
        setFormError("Error de red. Intenta otra vez.");
      }
    } finally {
      setImpersonatingAdminId(null);
    }
  }

  // «Torneos recientes»: las lápidas de gobierno conservan su fecha de
  // creación y se colaban aquí (Eduardo 2026-10-03). El servidor ya excluye
  // los archivados ANTES de tomar los 6 más nuevos; este filtro es solo un
  // respaldo por si llegara alguno.
  const { visible: recentTournaments, hiddenCount: recentArchivedHidden } = splitArchived(
    dashboard?.recent_tournaments ?? [],
    false,
  );

  const outboxPending = dashboard?.communications.outbox_pending ?? null;

  function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
  }

  return (
    <div className="adm-players sa-home">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Superadmin · toda la plataforma</span>
          <h1 className="adm-players__title">Hola, {user?.full_name ?? "Superadmin"}</h1>
        </div>
        <div className="adm-players__head-actions">
          <Link className="adm-players__btn adm-players__btn--primary sa-link-btn" to="/admin/tournaments/new">
            + Nuevo torneo
          </Link>
        </div>
      </header>

      {dashboardError && (
        <p role="alert" className="form-error">
          {dashboardError}
        </p>
      )}

      <section className="sa-pending" aria-label="Pendientes">
        <Link to="/superadmin/support-tickets" className={`sa-pending__tile${openTickets ? " is-hot" : ""}`}>
          <small>Tickets abiertos</small>
          <strong>{openTickets ?? "—"}</strong>
          <span>Revisar ›</span>
        </Link>
        <Link to="/superadmin/demo-requests" className={`sa-pending__tile${newDemoRequests ? " is-hot" : ""}`}>
          <small>Solicitudes de demo nuevas</small>
          <strong>{newDemoRequests ?? "—"}</strong>
          <span>Ver solicitudes ›</span>
        </Link>
        <div className={`sa-pending__tile${outboxPending ? " is-warn" : ""}`}>
          <small>Avisos sin enviar</small>
          <strong>{outboxPending ?? "—"}</strong>
          <span>En la cola de envío</span>
        </div>
      </section>

      <section className="sa-metrics" aria-label="Métricas globales">
        <div className="sa-metrics__item">
          <small>Torneos activos</small>
          <strong>{dashboard?.tournaments.active ?? 0}</strong>
          <span>
            {dashboard?.tournaments.total ?? 0} en total · {dashboard?.tournaments.closed ?? 0} cerrados ·{" "}
            {dashboard?.tournaments.archived ?? 0} archivados
          </span>
        </div>
        <div className="sa-metrics__item">
          <small>Organizadores</small>
          <strong>{dashboard?.admins.total ?? 0}</strong>
          <span>{dashboard?.admins.tournament_assignments ?? 0} asignaciones a torneos</span>
        </div>
        <div className="sa-metrics__item">
          <small>Jugadores</small>
          <strong>{(dashboard?.players.total_active ?? 0).toLocaleString("es-MX")}</strong>
          <span>perfiles activos</span>
        </div>
        <div className="sa-metrics__item sa-metrics__item--lime">
          <small>Fees pendientes</small>
          <strong>{fees ? formatMoney(fees.pending, fees.currency) : "—"}</strong>
          <span>de torneos por cobrar</span>
        </div>
      </section>

      <nav className="sa-shortcuts" aria-label="Atajos">
        <Link className="sa-shortcuts__tile" to="/superadmin/tournaments">
          <strong>Gobierno de torneos</strong>
          <small>Asignar dueño o borrar</small>
        </Link>
        <Link className="sa-shortcuts__tile" to="/superadmin/fees">
          <strong>Fees de plataforma</strong>
          <small>Comisión por torneo</small>
        </Link>
        <Link className="sa-shortcuts__tile" to="/admin/players">
          <strong>Jugadores</strong>
          <small>Catálogo global</small>
        </Link>
        <div className="sa-shortcuts__tile sa-shortcuts__tile--more">
          <strong>Más</strong>
          <small>
            <Link to="/superadmin/fees-organizadores">Deuda de organizadores</Link> · <Link to="/superadmin/clubs">Clubes</Link> · <Link to="/superadmin/membresias">Membresías</Link> · <Link to="/superadmin/rankings">Rankings</Link> ·{" "}
            <Link to="/superadmin/demo-requests">Demos</Link> · <Link to="/superadmin/intentos-inscripcion">Intentos fallidos de inscripción</Link> · <Link to="/admin/tournaments">Torneos</Link> ·{" "}
            <Link to="/superadmin/support-tickets">Tickets</Link>
          </small>
        </div>
      </nav>

      <section className="adm-players__panel">
        <div className="sa-section-head">
          <h2 className="adm-players__create-title">Organizadores</h2>
          <button
            type="button"
            className="adm-players__btn adm-players__btn--secondary"
            aria-expanded={showCreate}
            onClick={() => setShowCreate((value) => !value)}
          >
            {showCreate ? "Cerrar" : "+ Crear organizador"}
          </button>
        </div>

        {showCreate && (
          <form className="form sa-create" onSubmit={handleCreate}>
            <label>
              Nombre completo
              <input
                type="text"
                required
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Correo
              <input
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Teléfono (opcional)
              <input
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Contraseña
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting}
              />
            </label>
            <button className="btn" type="submit" disabled={submitting}>
              {submitting ? "Creando…" : "Crear organizador"}
            </button>
          </form>
        )}

        {formError && (
          <p role="alert" className="form-error">
            {formError}
          </p>
        )}
        {formMessage && <p className="form-message">{formMessage}</p>}
        {listError && (
          <p role="alert" className="form-error">
            {listError}
          </p>
        )}

        {admins.length > 0 && (
          <div className="sa-org-search">
            <input
              type="search"
              aria-label="Buscar organizador"
              placeholder="Buscar por nombre, correo o teléfono"
              value={adminQuery}
              onChange={(event) => setAdminQuery(event.target.value)}
            />
            {adminQuery.trim() && (
              <small className="muted">
                {visibleAdmins.length} de {admins.length}
              </small>
            )}
          </div>
        )}

        {admins.length === 0 ? (
          <p className="muted">Aún no hay organizadores registrados.</p>
        ) : visibleAdmins.length === 0 ? (
          <p className="muted">Ningún organizador coincide con «{adminQuery.trim()}».</p>
        ) : (
          <div className="adm-players__cards">
            {visibleAdmins.map((admin) => {
              const disabled = admin.status === "disabled";
              const menuOpen = menuAdminId === admin.id;
              return (
                <article key={admin.id} className="adm-players__card sa-org">
                  <div className="sa-org__row">
                    <div className="adm-players__who">
                      <span className="adm-players__avatar">{initials(admin.full_name)}</span>
                      <div className="adm-players__who-text">
                        <strong>{admin.full_name}</strong>
                        <span className="adm-players__sub">
                          {admin.email}
                          {admin.phone ? ` · ${admin.phone}` : ""} · {describeLastAccess(admin.last_access_at)}
                        </span>
                      </div>
                    </div>
                    <div className="adm-players__actions">
                      {admin.roles.includes("soporte") ? (
                        <span className="adm-players__chip adm-players__chip--support">{SUPPORT_ROLE_LABEL}</span>
                      ) : null}
                      <span className={`adm-players__chip${disabled ? "" : " adm-players__chip--on"}`}>
                        {disabled ? "Inactivo" : "Activo"}
                      </span>
                      <button
                        className="btn btn--compact adm-players__edit"
                        type="button"
                        onClick={() => void handleImpersonateAdmin(admin)}
                        disabled={impersonatingAdminId === admin.id || disabled}
                      >
                        {impersonatingAdminId === admin.id ? "Entrando…" : "Ver como admin"}
                      </button>
                      <div className="adm-players__menu" data-row-menu>
                        <button
                          type="button"
                          className="btn btn--ghost btn--compact adm-players__menu-trigger"
                          aria-haspopup="menu"
                          aria-expanded={menuOpen}
                          aria-label={`Más acciones para ${admin.full_name}`}
                          onClick={() => setMenuAdminId(menuOpen ? null : admin.id)}
                        >
                          ⋯
                        </button>
                        {menuOpen ? (
                          <div className="adm-players__menu-list" role="menu">
                            <button
                              type="button"
                              role="menuitem"
                              className="adm-players__menu-item"
                              onClick={() => {
                                setMenuAdminId(null);
                                setResetOpenId(admin.id);
                              }}
                            >
                              Resetear contraseña
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="adm-players__menu-item"
                              disabled={statusBusyId === admin.id}
                              onClick={() => {
                                setMenuAdminId(null);
                                void handleToggleStatus(admin);
                              }}
                            >
                              {disabled ? "Reactivar" : "Inactivar"}
                            </button>
                            {!admin.managed_by_user_id ? (
                              <button
                                type="button"
                                role="menuitem"
                                className="adm-players__menu-item"
                                disabled={statusBusyId === admin.id}
                                onClick={() => {
                                  setMenuAdminId(null);
                                  void handleToggleSupport(admin);
                                }}
                              >
                                {admin.roles.includes("soporte")
                                  ? `Quitar ${SUPPORT_ROLE_LABEL}`
                                  : `Hacer ${SUPPORT_ROLE_LABEL}`}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              role="menuitem"
                              className="adm-players__menu-item adm-players__menu-item--danger"
                              title="Solo se puede eliminar un admin sin historia; con torneos o cobros, inactívalo."
                              onClick={() => {
                                setMenuAdminId(null);
                                void handleDeleteAdmin(admin);
                              }}
                            >
                              Eliminar
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {statusBusyId === admin.id ? <p className="muted sa-org__note">Cambiando estado…</p> : null}

                  {resetOpenId === admin.id ? (
                    <div className="sa-org__inline">
                      <input
                        type="password"
                        placeholder="Nueva contraseña"
                        minLength={6}
                        value={resetPasswords[admin.id] ?? ""}
                        onChange={(event) =>
                          setResetPasswords((prev) => ({
                            ...prev,
                            [admin.id]: event.target.value,
                          }))
                        }
                        disabled={resettingAdminId === admin.id}
                        aria-label={`Nueva contraseña para ${admin.email}`}
                      />
                      <button
                        className="btn btn--compact"
                        type="button"
                        onClick={() => void handleResetPassword(admin)}
                        disabled={
                          resettingAdminId === admin.id ||
                          (resetPasswords[admin.id]?.trim().length ?? 0) < 6
                        }
                      >
                        {resettingAdminId === admin.id ? "Actualizando…" : "Resetear contraseña"}
                      </button>
                      <button
                        className="btn btn--ghost btn--compact"
                        type="button"
                        onClick={() => setResetOpenId(null)}
                      >
                        Cerrar
                      </button>
                    </div>
                  ) : null}

                  {armedDeleteId === admin.id ? (
                    <div className="sa-org__inline sa-org__inline--danger">
                      <span>Solo se elimina un organizador sin historia; con torneos o cobros, inactívalo.</span>
                      <button
                        className="btn btn--danger btn--compact"
                        type="button"
                        onClick={() => void handleDeleteAdmin(admin)}
                        disabled={deletingId === admin.id}
                      >
                        {deletingId === admin.id ? "Eliminando…" : "¿Confirmar eliminación?"}
                      </button>
                      <button
                        className="btn btn--ghost btn--compact"
                        type="button"
                        onClick={() => setArmedDeleteId(null)}
                        disabled={deletingId === admin.id}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="adm-players__panel">
        <div className="sa-section-head">
          <div>
            <h2 className="adm-players__create-title">Torneos recientes</h2>
            <p className="muted sa-section-sub">
              {recentTournaments.length} torneo(s) creado(s)
              {recentArchivedHidden > 0 ? ` · ${archivedHiddenNote(recentArchivedHidden)}` : ""}.
            </p>
          </div>
          <Link className="adm-players__btn adm-players__btn--secondary sa-link-btn" to="/admin/tournaments">
            Ver todos
          </Link>
        </div>
        {!dashboard ? (
          <p className="muted">Cargando…</p>
        ) : dashboard.recent_tournaments.length === 0 ? (
          <p className="muted">Aún no hay torneos creados.</p>
        ) : recentTournaments.length === 0 ? (
          <p className="muted" data-no-toast>
            Los torneos más recientes están archivados. «Ver todos» muestra los demás.
          </p>
        ) : (
          <div className="sa-recent">
            {recentTournaments.map((row) => (
              <div key={row.id} className="adm-tourn__row">
                <div className="adm-tourn__row-name">
                  <Link to={`/admin/tournaments/${row.id}`}>{row.name}</Link>
                  <small>
                    {row.tournament_type} · {row.players_count} jugadores
                  </small>
                </div>
                <span className={`adm-tourn__chip${row.status === "active" ? " adm-tourn__chip--live" : ""}`}>
                  {superadminStatusLabel(row.status)}
                </span>
                <div className="sa-recent__links">
                  <Link className="adm-tourn__chip" to={`/admin/tournaments/${row.id}/registrations`}>
                    Inscripción
                  </Link>
                  <Link className="adm-tourn__chip" to={`/admin/tournaments/${row.id}/standings`}>
                    Standings
                  </Link>
                  <Link className="adm-tourn__chip" to={`/admin/tournaments/${row.id}/schedule`}>
                    Calendario
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function foldText(value: string | null | undefined): string {
  return (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/** Busca por nombre, correo o teléfono sin acentos ni mayúsculas; el
 * teléfono compara solo dígitos («33 1234» encuentra 3312345678). */
export function filterAdmins(admins: AuthUser[], query: string): AuthUser[] {
  const text = foldText(query).trim();
  if (!text) return admins;
  // Solo si parece teléfono (sin letras): «ana2@x» no debe buscar «2» en celulares.
  const digits = /[a-z@]/i.test(query) ? "" : query.replace(/\D/g, "");
  return admins.filter((admin) => {
    if (foldText(admin.full_name).includes(text) || foldText(admin.email).includes(text)) return true;
    return digits.length > 0 && (admin.phone ?? "").replace(/\D/g, "").includes(digits);
  });
}
